import React from "react";

import { clsx } from "clsx";

import { twMerge } from "tailwind-merge";
import { Lock } from "lucide-react";

type CardAccent = "none" | "critical" | "warning" | "success" | "info";

const accentClasses: Record<Exclude<CardAccent, "none">, string> = {
  critical: "border-l-4 border-l-red-500",
  warning: "border-l-4 border-l-yellow-500",
  success: "border-l-4 border-l-green-500",
  info: "border-l-4 border-l-blue-500",
};

interface CardProps {
  className?: string;
  headerText?: string;
  descriptionText?: string;
  icon?: React.ReactNode;
  headerAction?: React.ReactNode;
  locked?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
  clickable?: boolean;
  accent?: CardAccent;
}

export function CardSkeleton() {
  return (
    <div className="border-borderSubtle flex w-full flex-col gap-5 rounded-lg border bg-white p-5 shadow dark:bg-gray-100">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 min-w-12 animate-pulse rounded bg-gray-200" />
        <div className="flex w-full justify-between gap-2">
          <div className="flex-1">
            <div className="mb-2 h-6 animate-pulse rounded bg-gray-200" />
            <div className="h-4 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </div>
      <div className="pb-2">
        <div className="h-9 w-24 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
}

export function Card({
  className,
  headerText,
  descriptionText,
  icon,
  headerAction,
  locked,
  children,
  onClick,
  clickable,
  accent = "none",
}: CardProps) {
  const isClickable = clickable || !!onClick;

  return (
    <div
      className={twMerge(
        clsx(
          "border-borderSubtle flex w-full flex-col justify-between gap-5 rounded-lg border bg-white p-5 shadow dark:bg-gray-100",
          children && "pb-2",
          isClickable &&
            "cursor-pointer transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:hover:bg-gray-200",
          accent !== "none" && accentClasses[accent],
          className,
        ),
      )}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!isClickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div className="flex w-full justify-between gap-2">
          <div>
            {headerText && (
              <h4 className="line-clamp-2 text-lg leading-6 font-bold">
                {headerText}
              </h4>
            )}
            {descriptionText && (
              <span className="text-textSubtle text-sm">{descriptionText}</span>
            )}
          </div>
          {(locked || headerAction) && (
            <div className="flex items-center gap-2 self-start">
              {locked && <Lock size={20} className="text-iconMinimal" />}
              {headerAction}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
